import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Collapse, Paper, Stack, Typography } from "@mui/material";
import DonutChart from "components/Dashboard/DonutChart";
import { useMemo, useState } from "react";

import { DONUT_COLORS } from "./scoutContextAnalysisFormat";
import { cacheReadRatePct, prettySpendBucketType } from "./scoutContextAnalysisStats";
import { type ContextSummary } from "./useScoutConversationContextAnalysis";

export function ContextUsageSummary({ summary }: { summary: ContextSummary | undefined }) {
  const [expanded, setExpanded] = useState(false);
  const view = useContextUsageView(summary);

  return (
    <Paper variant="outlined" sx={{ flexShrink: 0 }}>
      <ProviderUsageHeader
        expanded={expanded}
        collapsedCaption={view.collapsedCaption}
        onToggle={() => setExpanded((value) => !value)}
      />
      <Collapse in={expanded}>
        <Box
          sx={{
            p: 2,
            pt: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <SpendAttributionDonut seriesData={view.seriesData} />
          <ContextUsageStatGrid view={view} />
        </Box>
      </Collapse>
    </Paper>
  );
}

function useContextUsageView(summary: ContextSummary | undefined) {
  const typeSlices = useMemo(() => {
    const byType = new Map<string, number>();
    for (const item of summary?.breakdown ?? []) {
      byType.set(item.type, (byType.get(item.type) ?? 0) + item.estimatedTokens);
    }
    return Array.from(byType.entries())
      .filter(([, tokens]) => tokens > 0)
      .map(([type, tokens]) => ({ type, tokens }))
      .sort((a, b) => b.tokens - a.tokens);
  }, [summary?.breakdown]);

  const cacheReadPct = useMemo(
    () =>
      summary?.cache
        ? cacheReadRatePct(summary.cache.cacheReadTokens, summary.cache.totalInputTokens)
        : null,
    [summary?.cache]
  );

  const totalTokens = summary?.totalEstimatedTokens ?? 0;
  const documentAttachmentCostUsd = summary?.documentAttachmentTotalEstimatedCostUsd ?? 0;
  const totalCostUsd = (summary?.totalRoundtripCostUsd ?? 0) + documentAttachmentCostUsd;
  const roundtripCount = summary?.roundtripCount ?? 0;

  const collapsedCaption = [
    `${totalTokens.toLocaleString()} tokens processed`,
    totalCostUsd > 0 ? `$${totalCostUsd.toFixed(4)}` : null,
    cacheReadPct != null ? `${cacheReadPct}% cache read` : null,
    summary ? `${summary.partCount.toLocaleString()} parts` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    seriesData: typeSlices.map((slice) => ({
      id: slice.type,
      value: slice.tokens,
      label: prettySpendBucketType(slice.type),
    })),
    totalTokens,
    totalCostUsd,
    cacheReadPct,
    roundtripCount,
    latestInputTokens: summary?.latestInputTokens ?? null,
    peakInputTokens: summary?.peakInputTokens ?? null,
    cache: summary?.cache,
    totalOutputTokens: summary?.totalOutputTokens ?? 0,
    totalThinkingTokens: summary?.totalThinkingTokens ?? 0,
    partCount: summary?.partCount ?? 0,
    collapsedCaption,
  };
}

type UsageView = ReturnType<typeof useContextUsageView>;

function ProviderUsageHeader({
  expanded,
  collapsedCaption,
  onToggle,
}: {
  expanded: boolean;
  collapsedCaption: string;
  onToggle: () => void;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 2,
        py: 1,
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { backgroundColor: "action.hover" },
      }}
      onClick={onToggle}
      role="button"
      aria-expanded={expanded}
      aria-label={expanded ? "Collapse provider usage" : "Expand provider usage"}
    >
      <Typography variant="subtitle2">Provider usage</Typography>
      {!expanded ? (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {collapsedCaption}
        </Typography>
      ) : null}
      <Box sx={{ flex: 1 }} />
      {expanded ? (
        <ExpandLess fontSize="small" color="action" />
      ) : (
        <ExpandMore fontSize="small" color="action" />
      )}
    </Stack>
  );
}

function SpendAttributionDonut({
  seriesData,
}: {
  seriesData: Array<{ id: string; value: number; label: string }>;
}) {
  return (
    <Box
      sx={{
        flex: "0 1 320px",
        width: 320,
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        Spend attribution (counted per call)
      </Typography>
      {seriesData.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          No provider usage recorded yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <DonutChart
            containerHeight="200px"
            seriesData={seriesData}
            seriesOverrides={{ innerRadius: 56, outerRadius: 90 }}
            selectedIndex={null}
            legend={{ position: "below", direction: "row" }}
            colors={DONUT_COLORS}
            legendSx={{ flexWrap: "wrap", rowGap: 0.5, maxWidth: "100%", px: 0 }}
          />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        Totals sum every provider call. Resent history and the system prompt are counted each time.
        For window size, use Latest window or Per-roundtrip Input.
      </Typography>
    </Box>
  );
}

function ContextUsageStatGrid({ view }: { view: UsageView }) {
  const roundtripHint =
    view.roundtripCount > 0
      ? `${view.roundtripCount.toLocaleString()} model call${view.roundtripCount === 1 ? "" : "s"}`
      : undefined;
  const peakHint =
    view.peakInputTokens != null ? `peak ${view.peakInputTokens.toLocaleString()}` : undefined;
  const cacheHint =
    view.cache && view.cache.totalInputTokens > 0
      ? `${view.cache.cacheReadTokens.toLocaleString()} read · ${view.cache.cacheWriteTokens.toLocaleString()} write`
      : undefined;

  return (
    <Box
      sx={{
        flex: "1 1 360px",
        minWidth: 0,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(max(160px, calc((100% - 36px) / 4)), 1fr))",
        gap: 1.5,
        alignItems: "stretch",
        alignContent: "start",
      }}
    >
      <ContextStatCard
        label="Tokens processed"
        value={view.totalTokens.toLocaleString()}
        hint={roundtripHint}
      />
      <ContextStatCard
        label="Latest window"
        value={view.latestInputTokens != null ? view.latestInputTokens.toLocaleString() : "—"}
        hint={peakHint}
      />
      <ContextStatCard
        label="Estimated cost"
        value={view.totalCostUsd > 0 ? `$${view.totalCostUsd.toFixed(4)}` : "—"}
      />
      <ContextStatCard
        label="Cache read rate"
        value={view.cacheReadPct != null ? `${view.cacheReadPct}%` : "—"}
        hint={cacheHint}
      />
      <ContextStatCard label="Input" value={(view.cache?.totalInputTokens ?? 0).toLocaleString()} />
      <ContextStatCard label="Output" value={view.totalOutputTokens.toLocaleString()} />
      <ContextStatCard label="Thinking" value={view.totalThinkingTokens.toLocaleString()} />
      <ContextStatCard label="Parts" value={view.partCount.toLocaleString()} />
    </Box>
  );
}

function ContextStatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2, mt: 0.25 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}
